<script>
import boxService from "@/services/box";
import Spinner from "@/components/ui/Spinner";
import ModalBox from "@/components/settings/institution/ModalBox.vue";

export default {
  name: "BoxList",

  components: {
    Spinner,
    ModalBox,
  },

  props: {
    institution: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isLoading: false,
      boxes: [],
      isBoxModalVisible: false,
      selectedBox: null,
    };
  },

  created() {
    this.fetchBoxes();
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode === 27) this.close();
    },

    async fetchBoxes() {
      if (!this.institution?._id) return;
      this.isLoading = true;
      try {
        const boxes = await boxService.getBoxes(this.institution._id);
        this.boxes = boxes.map((b) => ({ ...b, screenIds: b.screenIds || [] }));
      } catch (e) {
        console.error("Error fetching boxes", e);
      } finally {
        this.isLoading = false;
      }
    },

    close() {
      this.$emit("close");
    },

    openCreateBox() {
      this.selectedBox = null;
      this.isBoxModalVisible = true;
    },

    openEditBox(box) {
      this.selectedBox = box;
      this.isBoxModalVisible = true;
    },

    closeBoxModal() {
      this.isBoxModalVisible = false;
      this.selectedBox = null;
    },

    onBoxSaved({ box, isUpdate }) {
      const normalized = { ...box, screenIds: box.screenIds || [] };
      if (isUpdate) {
        const idx = this.boxes.findIndex((b) => b._id === box._id);
        if (idx !== -1) this.$set(this.boxes, idx, normalized);
      } else {
        this.boxes.push(normalized);
      }
    },

    confirmDeleteBox(box) {
      this.$confirm(`¿Desea eliminar el box "${box.name}"?`, "Eliminar box", {
        confirmButtonText: "Eliminar",
        cancelButtonText: "Cancelar",
        type: "warning",
      })
        .then(() => this.deleteBox(box._id))
        .catch(() => {});
    },

    async deleteBox(id) {
      try {
        await boxService.deleteBox(id);
        this.boxes = this.boxes.filter((o) => o._id !== id);
      } catch (e) {
        console.error("Error deleting box", e);
      }
    },
  },
};
</script>

<template lang="pug">
  ValidationObserver(v-slot="{handleSubmit}")
    form.modal.box-list-page(@submit.prevent="handleSubmit(close)")
      header.modal__header
        h2.modal__title Boxes de {{ institution.name }}
        .modal__actions
          el-button(type="info" @click="close()") Cancelar
      p.modal__subtitle Gestión de boxes de la institución

      .modal__content(v-if="isLoading")
        spinner

      .modal__content(v-else)
        .modal__block
          .modal__section
            .modal__sign.sign
              .sign__icon
                micon(name="edit")
              h3.sign__title Boxes
            article.modal__fields
              .box-toolbar
                .button.button--blue.box__icon(@click="openCreateBox")
                  micon(name="add_circle_outline").button__icon
                  span.button__text Nuevo box

              table.box-table
                thead
                  tr
                    th Nombre
                    th Pantallas
                    th.actions-col Acciones
                tbody
                  tr(v-if="!boxes.length")
                    td.empty(colspan="3") No hay boxes cargados aún.
                  tr(v-for="box in boxes" :key="box._id")
                    td {{ box.name }}
                    td
                      .screen-tags(v-if="box.screenIds && box.screenIds.length")
                        el-tag(
                          v-for="id in box.screenIds"
                          :key="id"
                          size="small"
                          effect="plain"
                          :disable-transitions="true"
                        ) {{ id }}
                      span.empty-cell(v-else) —
                    td.actions
                      .actions-container
                        el-dropdown(trigger="click")
                          .button.button--action.el-dropdown-link(@click.stop)
                            micon(name="more_horiz")
                          el-dropdown-menu(slot="dropdown")
                            el-dropdown-item
                              div(@click="openEditBox(box)")
                                micon(name="edit")
                                | Editar box
                            el-dropdown-item
                              div(@click="confirmDeleteBox(box)")
                                micon(name="delete_forever")
                                | Eliminar box

      modal-box(
        v-if="isBoxModalVisible"
        :visible="isBoxModalVisible"
        :institution-id="institution._id"
        :current-box="selectedBox"
        @close="closeBoxModal"
        @saved="onBoxSaved"
      )
</template>

<style lang="scss" scoped>
.box-list-page.modal {
  z-index: 1500;
}

.box-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;
}

.box-table {
  width: 100%;
  border-collapse: collapse;

  thead th {
    text-align: left;
    padding: 12px;
    color: #040055;
    font-weight: 700;
    font-size: 13px;
    border-bottom: 1px solid #eaeaea;

    &.actions-col {
      width: 100px;
      text-align: right;
    }
  }

  tbody tr {
    border-bottom: 1px solid #eaeaea;

    &:hover {
      background: #fafafa;
    }
  }

  td {
    padding: 14px 12px;
    vertical-align: middle;
    color: #303133;
    font-size: 14px;

    &.actions {
      text-align: right;
      width: 100px;
    }

    &.empty {
      text-align: center;
      color: #909399;
      font-style: italic;
      padding: 24px;
    }
  }

  .empty-cell {
    color: #c0c4cc;
  }

  .screen-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }
}
</style>
